import { useState } from 'react';
import { createFolder, updateFolder, deleteFolder } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { FREE_FOLDER_LIMIT } from '../context/AuthContext';

const PRESET_COLORS = [
  { hex: '#7f77dd', name: 'Purple'  },
  { hex: '#1d9e75', name: 'Green'   },
  { hex: '#e24b4a', name: 'Red'     },
  { hex: '#ef9f27', name: 'Amber'   },
  { hex: '#85b7eb', name: 'Blue'    },
  { hex: '#f09595', name: 'Pink'    },
  { hex: '#5dcaa5', name: 'Teal'    },
  { hex: '#afa9ec', name: 'Lavender'},
  { hex: '#94a3b8', name: 'Slate'   },
  { hex: '#f97316', name: 'Orange'  },
];

function ColorPicker({ value, onChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const isPreset = PRESET_COLORS.some(c => c.hex === value);

  return (
    <div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--dim)', marginBottom: 8 }}>
        Folder color
      </div>

      {/* Preview swatch */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: value,
          boxShadow: `0 2px 8px ${value}60`, flexShrink: 0,
          border: '2px solid rgba(255,255,255,0.15)',
        }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
            {PRESET_COLORS.find(c => c.hex === value)?.name || 'Custom'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--dim)', fontFamily: 'var(--mono)' }}>{value}</div>
        </div>
      </div>

      {/* Preset swatches */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
        {PRESET_COLORS.map(c => (
          <button
            key={c.hex}
            title={c.name}
            onClick={() => { onChange(c.hex); setShowCustom(false); }}
            style={{
              width: 28, height: 28, borderRadius: 7, background: c.hex,
              border: value === c.hex ? '2.5px solid var(--text)' : '2px solid transparent',
              cursor: 'pointer', padding: 0, flexShrink: 0,
              boxShadow: value === c.hex ? `0 0 0 1px ${c.hex}` : 'none',
              transition: 'transform 0.1s, border 0.1s',
              transform: value === c.hex ? 'scale(1.15)' : 'scale(1)',
            }}
          />
        ))}

        {/* Custom color button */}
        <button
          title="Custom color"
          onClick={() => setShowCustom(s => !s)}
          style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'var(--surface2)',
            border: showCustom || !isPreset ? '2px solid var(--accent)' : '1.5px dashed var(--border2)',
            cursor: 'pointer', padding: 0, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: 'var(--muted)', transition: 'all 0.1s',
          }}
        >
          {showCustom || !isPreset ? (
            <div style={{ width: 14, height: 14, borderRadius: 3, background: isPreset ? 'var(--dim)' : value }} />
          ) : '+'}
        </button>
      </div>

      {/* Custom hex input */}
      {(showCustom || !isPreset) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Native color picker */}
          <label style={{ position: 'relative', cursor: 'pointer' }}>
            <div style={{
              width: 36, height: 28, borderRadius: 6, background: value,
              border: '0.5px solid var(--border2)', cursor: 'pointer',
            }} />
            <input
              type="color"
              value={value}
              onChange={e => onChange(e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
            />
          </label>
          <input
            type="text"
            value={value}
            onChange={e => {
              const v = e.target.value;
              if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
            }}
            maxLength={7}
            placeholder="#000000"
            style={{
              flex: 1, background: 'var(--bg)', border: '0.5px solid var(--border2)',
              borderRadius: 6, padding: '6px 10px', color: 'var(--text)',
              fontFamily: 'var(--mono)', fontSize: 12, outline: 'none',
            }}
          />
          <div style={{
            width: 12, height: 12, borderRadius: '50%', background: /^#[0-9a-fA-F]{6}$/.test(value) ? value : 'var(--border2)',
            flexShrink: 0, boxShadow: /^#[0-9a-fA-F]{6}$/.test(value) ? `0 0 6px ${value}` : 'none',
          }} />
        </div>
      )}
    </div>
  );
}

export default function FolderManager({ folders, onChanged, onClose }) {
  const { isPro, isAdmin } = useAuth();
  const canCreateMore = isPro || isAdmin || folders.length < FREE_FOLDER_LIMIT;
  const { user } = useAuth();
  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0].hex);
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleCreate = async () => {
    if (!newName.trim()) return;
    if (!canCreateMore) {
      setError(`Free accounts are limited to ${FREE_FOLDER_LIMIT} folders. Upgrade to Pro for unlimited folders.`);
      return;
    }
    setSaving(true); setError('');
    try {
      const folder = await createFolder(user.id, newName.trim(), newColor);
      onChanged([...folders, folder]);
      setNewName(''); setNewColor(PRESET_COLORS[0].hex);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true); setError('');
    try {
      const updated = await updateFolder(id, { name: editName.trim(), color: editColor });
      onChanged(folders.map(f => f.id === id ? updated : f));
      setEditId(null);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this folder? Entries inside will be unfoldered.')) return;
    try {
      await deleteFolder(id);
      onChanged(folders.filter(f => f.id !== id));
    } catch (e) { setError(e.message); }
  };

  const startEdit = (f) => {
    setEditId(f.id);
    setEditName(f.name);
    setEditColor(f.color || PRESET_COLORS[0].hex);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <span style={s.title}>Manage folders</span>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Create new folder */}
        <div style={s.section}>
          <div style={s.sectionLabel}>New folder</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              style={s.input}
              placeholder="Folder name..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
            <ColorPicker value={newColor} onChange={setNewColor} />
            <button
              style={{ ...s.createBtn, opacity: !newName.trim() || saving ? 0.5 : 1 }}
              onClick={handleCreate}
              disabled={saving || !canCreateMore}
              disabled={saving || !newName.trim()}>
              {saving ? 'Creating...' : `+ Create "${newName || 'folder'}"`}
            </button>
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        {/* Existing folders */}
        {folders.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionLabel}>Your folders ({folders.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {folders.map(f => (
                <div key={f.id}>
                  {editId === f.id ? (
                    /* Edit mode */
                    <div style={{ ...s.folderRow, flexDirection: 'column', alignItems: 'stretch', gap: 12, padding: '14px 14px' }}>
                      <input
                        style={s.input}
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleUpdate(f.id)}
                        autoFocus
                      />
                      <ColorPicker value={editColor} onChange={setEditColor} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={s.saveBtn} onClick={() => handleUpdate(f.id)} disabled={saving}>
                          {saving ? 'Saving...' : 'Save changes'}
                        </button>
                        <button style={s.cancelBtn} onClick={() => setEditId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div style={s.folderRow}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                        background: f.color || PRESET_COLORS[0].hex,
                        boxShadow: `0 2px 6px ${f.color || PRESET_COLORS[0].hex}50`,
                      }} />
                      <span style={s.folderName}>{f.name}</span>
                      <span style={s.folderCount}>
                        {f.entries?.[0]?.count ?? 0} entries
                      </span>
                      <button
                        style={s.iconBtn}
                        onClick={() => startEdit(f)}
                        title="Edit folder">
                        ✎
                      </button>
                      <button
                        style={{ ...s.iconBtn, color: 'var(--danger)' }}
                        onClick={() => handleDelete(f.id)}
                        title="Delete folder">
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {folders.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 20px', color: 'var(--dim)', fontSize: 13 }}>
            No folders yet — create one above
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(3px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9000, padding: 16,
  },
  modal: {
    background: 'var(--surface)', border: '0.5px solid var(--border2)',
    borderRadius: 16, width: '100%', maxWidth: 440,
    maxHeight: '90vh', overflow: 'auto', fontFamily: 'var(--font)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '0.5px solid var(--border)',
    position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
  },
  title: { fontSize: 15, fontWeight: 500, color: 'var(--text)' },
  closeBtn: {
    background: 'none', border: 'none', color: 'var(--dim)',
    fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 2,
  },
  section: { padding: '18px 20px', borderBottom: '0.5px solid var(--border)' },
  sectionLabel: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em',
    color: 'var(--dim)', marginBottom: 12, fontWeight: 500,
  },
  input: {
    background: 'var(--bg)', border: '0.5px solid var(--border2)',
    borderRadius: 8, padding: '9px 12px', width: '100%',
    color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none',
  },
  createBtn: {
    background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 8, padding: '10px 16px', width: '100%',
    fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  folderRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px',
  },
  folderName: {
    flex: 1, fontSize: 13, color: 'var(--text)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  folderCount: { fontSize: 11, color: 'var(--dim)', flexShrink: 0 },
  iconBtn: {
    background: 'none', border: 'none', color: 'var(--muted)',
    cursor: 'pointer', fontSize: 14, padding: '3px 6px', flexShrink: 0, borderRadius: 4,
  },
  saveBtn: {
    flex: 1, background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 8, padding: '9px 14px', fontFamily: 'var(--font)',
    fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none', border: '0.5px solid var(--border2)',
    borderRadius: 8, padding: '8px 14px', fontFamily: 'var(--font)',
    fontSize: 13, color: 'var(--muted)', cursor: 'pointer',
  },
  error: {
    margin: '0 20px 12px', fontSize: 12, color: 'var(--danger)',
    background: 'rgba(226,75,74,0.1)', borderRadius: 8, padding: '8px 12px',
  },
};
